import { type Reducer } from 'react'

import { isNil } from 'helpers/utils'
import type { ReducerAction, PageMeta, PageData, WidgetSlotData } from 'types'
import type { WidgetTypes } from 'widgets'
import type { BannerDetail, OfferDetail } from 'fdp-tracker/customDataTypes'

export interface BottomSheetData {
  pageMeta?: PageMeta | null
  pageData?: PageData | null
  slots?: WidgetSlotData[]
}

export interface TrackingData {
  totalOfferBannerCount?: number
  offerType?: string[]
  planCircle?: string
  planOperator?: string
  userType?: string
  bannerType?: string[]
  contactPermissionGranted?: boolean
  totalBannerCount?: number
  searchUsed?: boolean
  amount?: number
  dueStatus?: boolean
  billerName?: string
  billerDetails?: string
  categoryType?: string
  bannerDetails?: BannerDetail[]
  offerBannerDetails?: OfferDetail[]
  paymentInfo?: Record<string, string | boolean | number | undefined | string[]>
}
export interface State {
  pageMeta?: PageMeta
  pageData?: PageData
  slots?: WidgetSlotData[]
  bottomSheetData?: BottomSheetData
  bottomSheetOpen?: boolean
  bottomSheetUri?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  widgetData?: Record<any, any>
  totalOfferBannerCount?: number
  offerType?: string[]
  planCircle?: string
  planId?: string
  rechargeData?: Record<string, string | number | undefined>
  connectionId?: string
  planOperator?: string
  userType?: string
  bannerType?: string[]
  contactPermissionGranted?: boolean
  amount?: number
  totalBannerCount?: number
  trackingInfo?: TrackingData
  searchUsed?: boolean
  dueStatus?: boolean
  billerName?: string
  billerDetails?: string
  categoryType?: string
  bannerDetails?: BannerDetail[]
  offerBannerDetails?: OfferDetail[]
  paymentInfo?: Record<string, string | boolean | number | undefined>
  canShowRevampedPBSDesign?: boolean
  callBackFn?: () => void
  canShowChangeNumber: boolean
  isRecharge: boolean
  expanded?: boolean
  showPrepaidLoader?: boolean
  searchedContact?: string
  openPullDownContainer?: boolean
}

export interface SetPageResponseActionPayload {
  pageMeta: PageMeta
  pageData: PageData
  slots: WidgetSlotData[]
}

export interface SetConnectionDetailsPayload {
  connectionId: string
  planId: string
  rechargeData?: Record<string, string | number | undefined>
  category?: string
}

export interface SetBottomSheetResponseActionPayload {
  pageUri?: string
  pageMeta?: PageMeta | null
  pageData?: PageData | null
  slots?: WidgetSlotData[]
}

export interface OpenPullDownPayload {
  callBackFn: () => void
}

export interface ToggleBottomSheetActionPayload {
  bottomSheetOpen: boolean
  bottomSheetUri?: string
}

export interface SetTrackingDataActionPayload {
  totalOfferBannerCount?: number
  offerType?: string[]
  planCircle?: string
  planOperator?: string
  userType?: string
  bannerType?: string[]
  contactPermissionGranted?: boolean
  totalBannerCount?: number
  amount?: number
  searchUsed?: boolean
  dueStatus?: boolean
  billerName?: string
  billerDetails?: string
  categoryType?: string
  bannerDetails?: BannerDetail[]
  offerBannerDetails?: OfferDetail[]
  paymentInfo?: Record<string, string | boolean | number | undefined | string[]>
}

export interface SetMenuDataActionPayload {
  expanded?: boolean
}

export interface SetPrepaidLoaderPayload {
  showPrepaidLoader?: boolean
  searchedContact?: string
}

export interface SetPullDownContainerPayload {
  openPullDownContainer?: boolean
}

export interface SetWidgetDataActionPayload {
  widgetType: WidgetTypes
  // todo: replace any with a type WidgetDataTypes
  // that will be a list of all widgetData types imported from rome-models
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>
}

export interface SetEnablePaymentsRevampPayload {
  canShowRevampedPBSDesign: boolean
}

export interface HideNotANumber {
  canShowChangeNumber: boolean
}

export interface DoesHaveEditNumber {
  isRecharge: boolean
}

export type AppActionPayload =
  | SetPageResponseActionPayload
  | SetBottomSheetResponseActionPayload
  | ToggleBottomSheetActionPayload
  | SetWidgetDataActionPayload
  | SetTrackingDataActionPayload
  | SetConnectionDetailsPayload
  | SetEnablePaymentsRevampPayload
  | OpenPullDownPayload
  | HideNotANumber
  | DoesHaveEditNumber
  | SetMenuDataActionPayload
  | SetPrepaidLoaderPayload
  | SetPullDownContainerPayload

export const enum AppActionTypes {
  SetPageResponse = 'SET_PAGE_RESPONSE',
  SetBottomSheetResponse = 'SET_BOTTOM_SHEET_RESPONSE',
  ToggleBottomSheet = 'TOGGLE_BOTTOM_SHEET',
  SetWidgetData = 'SET_WIDGET_DATA',
  SetTrackingData = 'SET_TRACKING_DATA',
  SetPlanDetails = 'SET_PLAN_DETAILS',
  SetEnablePaymentsRevamp = 'ENABLE_PAYMENTS_REVAMP',
  OpenPullDown = 'OPEN_PULL_DOWN',
  HideNotANumber = 'NOT_A_NUMBER',
  DoesHaveEditNumber = 'DOES_HAVE_EDIT_NUMBER',
  SetMenuData = 'SET_MENU_DATA',
  SetPrepaidLoader = 'SET_PREPAID_LOADER',
  SetPullDownContainer = 'SET_PULLDOWN_CONTAINER'
}

// use-immer library handles the direct state mutation
const appReducer: Reducer<State, ReducerAction<AppActionPayload>> = (state, action) => {
  switch (action.type) {
    case AppActionTypes.SetPageResponse: {
      const { pageMeta, pageData, slots } = action.payload as SetPageResponseActionPayload
      state.pageMeta = pageMeta
      state.pageData = pageData
      state.slots = slots
      return state
    }
    case AppActionTypes.SetEnablePaymentsRevamp: {
      const { canShowRevampedPBSDesign } = action.payload as SetEnablePaymentsRevampPayload
      state.canShowRevampedPBSDesign = canShowRevampedPBSDesign
      return state
    }
    case AppActionTypes.SetPlanDetails: {
      const { planId, connectionId, rechargeData } = action.payload as SetConnectionDetailsPayload
      state.connectionId = connectionId
      state.planId = planId
      state.rechargeData = rechargeData
      return state
    }
    case AppActionTypes.SetBottomSheetResponse: {
      const { pageMeta, pageData, slots } = action.payload as SetBottomSheetResponseActionPayload
      state.bottomSheetData = {
        pageMeta,
        pageData,
        slots,
      }
      return state
    }
    case AppActionTypes.HideNotANumber: {
      const { canShowChangeNumber } = action.payload as HideNotANumber
      state.canShowChangeNumber = canShowChangeNumber
      return state
    }
    case AppActionTypes.DoesHaveEditNumber: {
      const { isRecharge } = action.payload as DoesHaveEditNumber
      state.isRecharge = isRecharge
      return state
    }
    case AppActionTypes.OpenPullDown: {
      const { callBackFn } = action.payload as OpenPullDownPayload
      state.callBackFn = callBackFn
      return state
    }
    case AppActionTypes.ToggleBottomSheet: {
      const { bottomSheetOpen, bottomSheetUri } = action.payload as ToggleBottomSheetActionPayload
      state.bottomSheetOpen = bottomSheetOpen
      state.bottomSheetUri = bottomSheetUri
      return state
    }
    case AppActionTypes.SetWidgetData: {
      const { widgetType, data } = action.payload as SetWidgetDataActionPayload
      if (isNil(state.widgetData)) {
        state.widgetData = {}
      }
      state.widgetData[widgetType] = {
        ...state.widgetData[widgetType],
        ...data,
      }
      return state
    }
    case AppActionTypes.SetTrackingData: {
      const {
        totalOfferBannerCount,
        offerType,
        planCircle,
        planOperator,
        userType,
        bannerType,
        contactPermissionGranted,
        totalBannerCount,
        searchUsed,
        dueStatus,
        billerName,
        billerDetails,
        categoryType,
        bannerDetails,
        offerBannerDetails,
        paymentInfo,
        amount,
      } = action.payload as SetTrackingDataActionPayload
      state.trackingInfo = {
        ...state.trackingInfo,
        totalOfferBannerCount:
          totalOfferBannerCount !== undefined
            ? totalOfferBannerCount
            : state?.trackingInfo?.totalOfferBannerCount,
        offerType: offerType ?? state?.trackingInfo?.offerType,
        planCircle: planCircle ?? state?.trackingInfo?.planCircle,
        planOperator: planOperator ?? state?.trackingInfo?.planOperator,
        userType: userType ?? state?.trackingInfo?.userType,
        bannerType: bannerType ?? state?.trackingInfo?.bannerType,
        contactPermissionGranted:
          contactPermissionGranted ?? state?.trackingInfo?.contactPermissionGranted ?? false,
        totalBannerCount: totalBannerCount ?? state?.trackingInfo?.totalBannerCount,
        searchUsed: searchUsed ?? state?.trackingInfo?.searchUsed ?? false,
        dueStatus: dueStatus ?? state?.trackingInfo?.dueStatus,
        billerName: billerName ?? state?.trackingInfo?.billerName,
        billerDetails: billerDetails ?? state?.trackingInfo?.billerDetails,
        categoryType: categoryType ?? state?.trackingInfo?.categoryType,
        bannerDetails: bannerDetails ?? state?.trackingInfo?.bannerDetails,
        offerBannerDetails: offerBannerDetails ?? state?.trackingInfo?.offerBannerDetails,
        paymentInfo: paymentInfo ?? state?.trackingInfo?.paymentInfo,
        amount: amount ?? state?.trackingInfo?.amount,
      }
      return state
    }
    case AppActionTypes.SetMenuData: {
      const { expanded } = action.payload as SetMenuDataActionPayload
      state.expanded = expanded
      return state
    }
    case AppActionTypes.SetPrepaidLoader: {
      const { showPrepaidLoader, searchedContact } = action.payload as SetPrepaidLoaderPayload
      state.showPrepaidLoader = showPrepaidLoader
      state.searchedContact = searchedContact
      return state
    }
    case AppActionTypes.SetPullDownContainer: {
      const { openPullDownContainer } = action.payload as SetPullDownContainerPayload
      state.openPullDownContainer = openPullDownContainer
      return state
    }
    default:
      return state
  }
}

export default appReducer
